import React, { useEffect, useContext, createContext } from 'react';
import { Box } from '@mui/material';
import gsap from 'gsap';
import { useScrollFadeIn } from '../../hooks/useScrollFadeIn';
import './ScrollFadeWrapper.css';

const ScrollFadeSectionContext = createContext(null);

export const ScrollFadeSection = ({
  children,
  sx = {},
  observerOptions = {},
}) => {
  const { ref, isVisible } = useScrollFadeIn(observerOptions);
  return (
    <ScrollFadeSectionContext.Provider value={isVisible}>
      <Box ref={ref} sx={sx}>
        {children}
      </Box>
    </ScrollFadeSectionContext.Provider>
  );
};

const ScrollFadeWrapper = ({
  children,
  sx = {},
  delay = 0,
  duration = 1.2,
  startingOpacity = 0,
  observerOptions = {},
  variant = 'bounce',
  twist = 0,
}) => {
  const sectionVisible = useContext(ScrollFadeSectionContext);
  const insideSection = sectionVisible !== null;

  const {
    ref,
    isVisible: selfVisible,
    pathname,
  } = useScrollFadeIn(observerOptions, insideSection);
  const isVisible = insideSection ? sectionVisible : selfVisible;

  // Reset GSAP state on mount and on every navigation so items re-animate.
  useEffect(() => {
    if (ref.current) {
      if (variant === 'text') {
        gsap.set(ref.current, { opacity: 0, y: 16 });
      } else {
        gsap.set(ref.current, {
          opacity: startingOpacity,
          scale: 0.9,
          rotation: twist,
        });
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (isVisible && ref.current) {
      if (variant === 'text') {
        gsap
          .timeline({ delay })
          .fromTo(
            ref.current,
            { y: 16, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' }
          );
      } else {
        gsap.timeline({ delay }).fromTo(
          ref.current,
          { scale: 0.9, opacity: startingOpacity, rotation: twist },
          {
            scale: 1,
            opacity: 1,
            rotation: 0,
            duration,
            ease: 'elastic.out(1.4, 0.75)',
          }
        );
      }
    }
  }, [isVisible]);

  return (
    <Box ref={ref} className="scroll-fade-container" sx={sx}>
      {children}
    </Box>
  );
};

export default ScrollFadeWrapper;
